// src/components/PortalHome.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../../supabaseClient';
import {
  BeakerIcon,
  VideoCameraIcon,
  CogIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/solid';

const PortalHome = () => {
  const navigate = useNavigate();

  // State variables to manage data, loading, and error states
  const [propertyScans, setPropertyScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  // Helper function to format date strings (if needed)
  const formatDate = (dateString) => {
    if (!dateString) return '';

    let isoString = dateString.replace(' ', 'T');
    if (/([+-]\d{2})$/.test(isoString)) {
      isoString += ':00';
    }

    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString);
      return 'Invalid Date';
    }

    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  };

  // Fetch data when the component mounts
  useEffect(() => {
    const fetchPropertyScans = async () => {
      setLoading(true);
      setError(null);

      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) {
          console.error('Error fetching user:', userError.message);
          setError(userError.message);
          setLoading(false);
          return;
        }

        setUser(user);

        const { data, error } = await supabase
          .from('property_scans')
          .select('*')
          .eq('user_id', user.id);

        if (error) {
          console.error('Error fetching property scans:', error.message);
          setError(error.message);
        } else {
          setPropertyScans(data);
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        setError('An unexpected error occurred.');
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyScans();
  }, []);

  // Navigation handlers
  const handleSeeAllProperties = () => {
    navigate('/portal/properties');
  };

  const handleTraining = (topic) => {
    alert('Coming soon')
    //navigate(`/portal/training/${topic}`);
  };

  const navigateToSettings = (setting) => {
    alert('Coming soon')
    //navigate(`/portal/settings/${setting}`);
  };

  return (
    <div className="p-8">
      <h2 className="text-3xl font-bold mb-6">Dashboard</h2>

      {/* Loading Indicator */}
      {loading && (
        <div className="mt-4 flex justify-center">
          <svg
            className="animate-spin h-5 w-5 text-gray-700"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            ></path>
          </svg>
        </div>
      )}

      {/* Error Message */}
      {error && <p className="mt-4 text-red-500">Error: {error}</p>}

      {/* Dashboard Tiles */}
      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Tile 1: Properties Scanned */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gray-700 p-4">
              <h3 className="text-xl font-semibold text-white flex items-center">
                <BeakerIcon className="h-6 w-6 mr-2" />
                Properties Scanned
              </h3>
            </div>
            <div className="p-6">
              <p className="text-4xl font-bold mb-4">{propertyScans.length}</p>
              <button
                onClick={handleSeeAllProperties}
                className="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600 transition duration-200"
              >
                See All
              </button>
            </div>
          </div>

          {/* Tile 2: Training Resources */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gray-700 p-4">
              <h3 className="text-xl font-semibold text-white flex items-center">
                <VideoCameraIcon className="h-6 w-6 mr-2" />
                Training Resources
              </h3>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => handleTraining('how-to-scan')}
                    className="text-gray-700 hover:underline flex items-center"
                  >
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    How to scan with the app
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleTraining('updating-property-scans')}
                    className="text-gray-700 hover:underline flex items-center"
                  >
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    Updating property scans
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleTraining('generating-reports')}
                    className="text-gray-700 hover:underline flex items-center"
                  >
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    Generating reports
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Tile 3: Settings */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gray-700 p-4">
              <h3 className="text-xl font-semibold text-white flex items-center">
                <CogIcon className="h-6 w-6 mr-2" />
                Settings
              </h3>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => navigateToSettings('report-styles')}
                    className="text-gray-700 hover:underline flex items-center"
                  >
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    Update Report Styles
                  </button>
                </li>
                {/* Add more settings links as needed */}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!loading && !error && propertyScans.length === 0 && (
        <p className="mt-4 text-gray-700">No property scans found.</p>
      )}
    </div>
  );
};

export default PortalHome;
